// @flow
import type {
    CountryLanguage,
    CountryDescription,
    EditAction,
    HiveAction,
    NewAction,
    Template
} from '~/core/types/beekeeper';
import type { Event } from '~/core/types/beekeeper/actions/events';
import type { Region } from '~/core/types/devices';
import type { Setting, SettingsLogic } from '~/core/constants/actions';
import type { ActionForm, ActionFormSettings, ActionFormAttribute, NormalisedTemplate } from './types';

import { curry, find, flowRight, identity, values } from 'lodash/fp';
import uuidv1 from 'uuid/v1';
import { DEFAULT_TEMPLATE, getAttributeDefinition } from '~/core/constants/actions';

// check if the minimum settings are present, otherwise use the default value
const validSettings = (
    logic: SettingsLogic,
    settingsDefinitions: { [string]: Setting },
    currentSettingsValues: ActionFormSettings,
    previousSettings = {}
) =>
    logic.reduce((acc, l) => {
        const definition = settingsDefinitions[l.settingId];
        const settingValue =
            currentSettingsValues[definition.propertyName] != null
                ? currentSettingsValues[definition.propertyName]
                : definition.defaultValue;

        const newSettingsValue = { ...acc, [definition.propertyName]: settingValue };

        const childrenLogic = l.ifValue && l.ifValue[settingValue];
        return childrenLogic
            ? validSettings(childrenLogic, settingsDefinitions, currentSettingsValues, newSettingsValue)
            : newSettingsValue;
    }, previousSettings);

const flattenEvents = (form: ActionForm<ActionFormAttribute>, region: Region) =>
    [...form.when, ...form.while, ...form.then].map(event => {
        const definition = getAttributeDefinition(event.type, region);
        const normaliseSettings = definition.normaliseSettings || identity;
        const settings = definition.settings
            ? {
                  settings: normaliseSettings(
                      validSettings(definition.settings.logic, definition.settings.definitions, event.settings || {})
                  )
              }
            : {};

        return {
            group: event.group,
            type: event.type,
            ...(event.id ? { id: event.id } : {}),
            ...(event.duration && event.duration > 0 ? { duration: event.duration } : {}),
            ...settings
        };
    });

export const formToNewAction = curry((region: Region, form: ActionForm<ActionFormAttribute>): NewAction => {
    return {
        enabled: form.enabled,
        entitlements: [],
        events: flattenEvents(form, region),
        name: form.name,
        template: `${form.template.id}.${form.template.version}`
    };
});

export const formToEditAction = curry(
    (region: Region, form: ActionForm<ActionFormAttribute>, actionId: string): EditAction => {
        return {
            enabled: form.enabled,
            entitlements: [],
            events: flattenEvents(form, region),
            name: form.name,
            id: actionId
        };
    }
);

type Groups = {
    when: Array<ActionFormAttribute>,
    while: Array<ActionFormAttribute>,
    then: Array<ActionFormAttribute>
};
export const actionToForm = (action: HiveAction): ActionForm<ActionFormAttribute> => {
    const groups: Groups = action.events.reduce(
        (acc, event) => {
            return {
                ...acc,
                [event.group]: [
                    ...acc[event.group],
                    {
                        ...event,
                        guid: uuidv1()
                    }
                ]
            };
        },
        { when: [], while: [], then: [] }
    );

    const template = (action.template || DEFAULT_TEMPLATE).split('.');
    return {
        ...(action.duration ? { duration: action.duration } : {}),
        enabled: action.enabled,
        id: action.id,
        name: action.name,
        status: 'edit',
        template: {
            id: template[0],
            version: template[1] ? parseInt(template[1], 10) : 0
        },
        ...groups
    };
};

export const transformEvent = (event: Event) => ({
    ...event,
    guid: uuidv1(),
    // Property cannot be accessed on any member of intersection type
    // $FlowFixMe
    ...(event.settings
        ? {
              settings: Object.keys(event.settings).reduce((sets, key) => {
                  // Flow complains about possible `event.settings` undefined
                  // but it is guaranteed to exist by condition above
                  // $FlowFixMe
                  return { ...sets, [key]: event.settings[key].value };
              }, {})
          }
        : {})
});

const localeMetaFromTemplate = (template: Template, locale: CountryLanguage): CountryDescription =>
    template.meta[locale] || flowRight(find(l => l.default), values)(template.meta) || { name: '', description: '' };

export const normaliseTemplate = (template: Template, locale: CountryLanguage): NormalisedTemplate => {
    const groups: Groups = template.events.reduce(
        (acc, event) =>
            event.required || event.default
                ? {
                      ...acc,
                      [event.group]: [...acc[event.group], transformEvent(event)]
                  }
                : acc,
        { when: [], while: [], then: [] }
    );

    const localeMeta = localeMetaFromTemplate(template, locale);

    return {
        description: localeMeta.description,
        name: localeMeta.name,
        tags: template.meta.tags,
        id: template.id,
        incompatible: template.incompatible || [],
        ...groups
    };
};



// WEBPACK FOOTER //
// ./src/core/state/actions/utils.js